<template>
  <div
    :class="['mls-form-root', embedded ? 'mls-form-root--embedded' : 'mf-overlay']"
    @click.self="onOverlayClick"
  >
    <!-- ====== Entry Card ====== -->
    <section class="mf-card">
      <header class="mf-card-head">
        <div class="mf-card-head__title">
          <h2 class="mf-card-title">
            Unit entry
            <span class="mf-unit-chip">
              <span class="mf-unit-chip__hash">#</span>{{ mlsUnit || "—" }}
            </span>
          </h2>
          <p class="mf-card-sub">
            Log pricing, sale, and supporting documentation for this unit.
          </p>
        </div>

        <div class="mf-seg" role="radiogroup" aria-label="Entry type">
          <input
            id="mf-entry-new"
            v-model="form.entry"
            type="radio"
            name="mf-entry"
            value="1"
          />
          <label for="mf-entry-new">New entry</label>
          <input
            id="mf-entry-original"
            v-model="form.entry"
            type="radio"
            name="mf-entry"
            value="2"
          />
          <label for="mf-entry-original">Original</label>
        </div>

        <button
          v-if="!embedded"
          type="button"
          class="mf-close"
          aria-label="Close"
          @click="$emit('close')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <form id="rental-update-form" @submit.prevent="onSubmit">
        <!-- ── Pricing ── -->
        <div class="mf-group">
          <span>Pricing</span><span class="mf-group__line"></span>
        </div>
        <div class="mf-grid">
          <div class="mf-field">
            <label for="mf-list-price">List price</label>
            <div class="mf-affix">
              <span class="mf-affix__sym">$</span>
              <input
                id="mf-list-price"
                v-model="form.listPrice"
                class="mf-input"
                type="text"
                inputmode="decimal"
                placeholder="0.00"
              />
            </div>
          </div>
          <div class="mf-field">
            <label for="mf-price-date">Price date</label>
            <input
              id="mf-price-date"
              v-model="form.priceDate"
              class="mf-input"
              type="date"
            />
          </div>
        </div>

        <!-- ── Sale ── -->
        <div class="mf-group">
          <span>Sale</span><span class="mf-group__line"></span>
        </div>
        <div class="mf-grid">
          <div class="mf-field">
            <label for="mf-sold-date">
              Sold date <span class="mf-opt">Optional</span>
            </label>
            <input
              id="mf-sold-date"
              v-model="form.soldDate"
              class="mf-input"
              type="date"
            />
          </div>
          <div class="mf-field">
            <label for="mf-sold-price">
              Sold price <span class="mf-opt">Optional</span>
            </label>
            <div class="mf-affix">
              <span class="mf-affix__sym">$</span>
              <input
                id="mf-sold-price"
                v-model="form.soldPrice"
                class="mf-input"
                type="text"
                inputmode="decimal"
                placeholder="0.00"
              />
            </div>
          </div>
          <div class="mf-field mf-field--span-2">
            <label for="mf-status">Status</label>
            <select
              id="mf-status"
              v-model="form.status"
              class="mf-select"
              required
            >
              <option :value="null" disabled>Select a status…</option>
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.text || "—" }}
              </option>
            </select>
          </div>
        </div>

        <!-- ── Backup & notes ── -->
        <div class="mf-group">
          <span>Backup &amp; notes</span><span class="mf-group__line"></span>
        </div>
        <div class="mf-grid">
          <div class="mf-field mf-field--span-2">
            <label for="mf-backup-file">
              Back-up file
              <span class="mf-opt">PDF, PNG, JPG · up to 25 MB</span>
            </label>
            <label
              class="mf-dropzone"
              :class="{
                'is-dragover': dragOver,
                'has-file': !!form.floorPlan,
              }"
              for="mf-backup-file"
              @dragenter.prevent="dragOver = true"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onFileDrop"
            >
              <span class="mf-dropzone__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </span>
              <span class="mf-dropzone__text">
                <span class="mf-dropzone__title">
                  <template v-if="form.floorPlan">
                    {{ form.floorPlan.name }}
                  </template>
                  <template v-else>
                    <span class="mf-dropzone__link">Click to upload</span>
                    or drag a file here
                  </template>
                </span>
                <span class="mf-dropzone__sub">
                  <template v-if="form.floorPlan">
                    {{ formatFileSize(form.floorPlan.size) }} · ready to upload
                  </template>
                  <template v-else>
                    MLS listing, signed offer, or supporting documentation.
                  </template>
                </span>
              </span>
              <input
                id="mf-backup-file"
                ref="fileInput"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                @change="onFileChange"
              />
              <button
                v-if="form.floorPlan"
                type="button"
                class="mf-dropzone__clear"
                aria-label="Remove file"
                @click.prevent.stop="clearFile"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
              </button>
            </label>
          </div>
          <div class="mf-field mf-field--span-2">
            <label for="mf-backup-text">
              Back-up notes <span class="mf-opt">Optional</span>
            </label>
            <textarea
              id="mf-backup-text"
              v-model="form.backupText"
              class="mf-input mf-textarea"
              placeholder="Context for this entry — source, conditions, anything a future reviewer should know."
            ></textarea>
          </div>
        </div>
      </form>

      <!-- ── Footer ── -->
      <footer class="mf-foot">
        <div class="mf-foot__left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          Submit creates a versioned log entry on this unit.
        </div>
        <div class="mf-foot__actions">
          <button
            type="button"
            class="mf-btn mf-btn--ghost"
            @click="$emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="rental-update-form"
            class="mf-btn mf-btn--primary"
            :disabled="submitting"
          >
            {{ submitting ? "Submitting…" : "Submit entry" }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        </div>
      </footer>
    </section>

    <!-- ====== Saved Response Card ====== -->
    <section v-if="mlsResponse" class="mf-card mf-card--success">
      <header class="mf-card-head">
        <div class="mf-card-head__title">
          <h2 class="mf-card-title">
            Latest entry
            <span class="mf-unit-chip">
              <span class="mf-unit-chip__hash">#</span>{{ mlsUnit || "—" }}
            </span>
          </h2>
          <p class="mf-card-sub">
            <span class="mf-pip"></span> Saved successfully
          </p>
        </div>
      </header>
      <div class="mf-summary">
        <dl class="mf-summary__row">
          <dt>List price</dt>
          <dd>{{ mlsResponse.list_price ? "$" + mlsResponse.list_price : "—" }}</dd>
        </dl>
        <dl class="mf-summary__row">
          <dt>Status</dt>
          <dd>{{ switchStatus(mlsResponse.status) || "—" }}</dd>
        </dl>
        <dl class="mf-summary__row">
          <dt>Sold price</dt>
          <dd>{{ mlsResponse.sold_price ? "$" + mlsResponse.sold_price : "—" }}</dd>
        </dl>
        <dl class="mf-summary__row">
          <dt>Sold date</dt>
          <dd>{{ mlsResponse.sold_date || "—" }}</dd>
        </dl>
        <dl class="mf-summary__row">
          <dt>List price date</dt>
          <dd>{{ mlsResponse.list_price_date || "—" }}</dd>
        </dl>
      </div>
    </section>
  </div>
</template>

<script>
import { BACKEND_URL } from "../../variables";

export default {
  props: {
    mlsCid: null,
    mlsUnit: null,
    embedded: { type: Boolean, default: false },
    closeOnBackdrop: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        cid: null,
        status: null,
        soldDate: "",
        soldPrice: null,
        listPrice: null,
        priceDate: null,
        newEntry: null,
        originalEntry: null,
        entry: "0",
        floorPlan: null,
        backupText: "",
      },
      statusOptions: [
        { text: "", value: 11482 },
        { text: "Active", value: 8197 },
        { text: "Sold", value: 8198 },
      ],
      mlsResponse: null,
      dragOver: false,
      submitting: false,
    };
  },
  mounted() {
    if (!this.embedded && this.$el && this.$el.nodeType === 1) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    if (!this.embedded && this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
  methods: {
    onOverlayClick() {
      if (!this.embedded && this.closeOnBackdrop) this.$emit("close");
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      this.form.floorPlan = file || null;
    },
    onFileDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer?.files && e.dataTransfer.files[0];
      if (!file) return;
      this.form.floorPlan = file;
      if (this.$refs.fileInput && typeof DataTransfer !== "undefined") {
        const dt = new DataTransfer();
        dt.items.add(file);
        this.$refs.fileInput.files = dt.files;
      }
    },
    clearFile() {
      this.form.floorPlan = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    formatFileSize(bytes) {
      if (bytes == null) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
      return (bytes / 1024 / 1024).toFixed(1) + " MB";
    },
    async onSubmit() {
      if (this.form.entry == 0) {
        this.form.originalEntry = false;
        this.form.newEntry = false;
      }

      if (this.form.entry == 1) {
        this.form.originalEntry = false;
        this.form.newEntry = true;
      }

      if (this.form.entry == 2) {
        this.form.originalEntry = true;
        this.form.newEntry = false;
      }

      let formData = new FormData();
      formData.append("cid", this.mlsCid);
      formData.append("status", this.form.status);
      formData.append("soldDate", this.form.soldDate);
      formData.append("soldPrice", this.form.soldPrice);
      formData.append("listPrice", this.form.listPrice);
      formData.append("priceDate", this.form.priceDate);
      formData.append("newEntry", this.form.newEntry);
      formData.append("originalEntry", this.form.originalEntry);
      formData.append("backupfile", this.form.floorPlan);
      formData.append("backupText", this.form.backupText);

      this.submitting = true;
      try {
        await $.ajax({
          type: "POST",
          url: BACKEND_URL + "api/presale_mls_rental_creation",
          data: formData,
          processData: false,
          contentType: false,
          success: () => {
            this.$bvToast.toast(`Saved successfully!`, {
              title: "Great job!",
              variant: "success",
              autoHideDelay: 5000,
            });
            this.$emit("fetchData");
          },
        }).done(function (response) {
          console.log(response);
        });
      } catch (error) {
        this.$bvToast.toast(`Save Failed. Please try again later.`, {
          title: "Ooops!",
          autoHideDelay: 5000,
          variant: "warning",
        });
      } finally {
        this.submitting = false;
      }
    },
    switchStatus(status) {
      switch (status) {
        case "8197":
          return "Active";

        case "8198":
          return "Sold";

        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.mls-form-root {
  font-family: var(--rpd-font-sans, "Inter Tight", "Inter", system-ui, sans-serif);
  color: var(--rpd-ink, #0a1320);
}

.mls-form-root--embedded {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Overlay (when rendered as a self-contained modal) */
.mf-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 40, 63, 0.55);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}

/* Shared sp-fade transition (matches other modals in the app) */
.sp-fade-enter-active,
.sp-fade-leave-active { transition: opacity 0.2s; }
.sp-fade-enter,
.sp-fade-leave-to { opacity: 0; }
.mf-overlay .mf-card {
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}
.mf-overlay .mf-card > form {
  flex: 1 1 auto;
  overflow-y: auto;
}
.mf-overlay .mf-card-head { position: relative; padding-right: 64px; }

/* Close button (modal mode) */
.mf-close {
  appearance: none;
  background: transparent;
  border: 1px solid var(--rpd-line, #e3e8ef);
  color: var(--rpd-muted, #5c6b80);
  width: 32px;
  height: 32px;
  border-radius: var(--rpd-r-sm, 6px);
  cursor: pointer;
  display: grid;
  place-items: center;
  position: absolute;
  top: 18px;
  right: 18px;
  transition: background 120ms ease, color 120ms ease,
    border-color 120ms ease;
}
.mf-close:hover {
  background: var(--rpd-bg-2, #f4f6f9);
  color: var(--rpd-ink, #0a1320);
  border-color: var(--rpd-navy-700, #1e3753);
}
.mf-close svg { width: 16px; height: 16px; }

/* ---------- Card ---------- */
.mf-card {
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-lg, 16px);
  box-shadow: var(--rpd-shadow-sm, 0 1px 2px rgba(14, 27, 44, 0.06));
  overflow: hidden;
}

.mf-card-head {
  padding: 22px 26px 18px;
  border-bottom: 1px solid var(--rpd-line-2, #eff2f6);
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
}

.mf-card-head__title { min-width: 0; flex: 1; }

.mf-card-title {
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--rpd-ink, #0a1320);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.mf-unit-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--rpd-font-mono, "JetBrains Mono", monospace);
  font-size: 0.75rem;
  font-weight: 500;
  background: var(--rpd-bg-2, #f4f6f9);
  color: var(--rpd-ink-2, #1a2638);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-pill, 999px);
  padding: 3px 10px;
  letter-spacing: 0.02em;
}
.mf-unit-chip__hash { color: var(--rpd-coral, #f26a6f); opacity: 0.9; }

.mf-card-sub {
  font-size: 0.875rem;
  color: var(--rpd-muted, #5c6b80);
  margin: 6px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mf-pip {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #1f9d55;
  box-shadow: 0 0 0 3px rgba(31, 157, 85, 0.16);
  display: inline-block;
}

/* ---------- Segmented ---------- */
.mf-seg {
  display: inline-flex;
  background: var(--rpd-bg-2, #f4f6f9);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-md, 10px);
  padding: 3px;
  gap: 2px;
  position: relative;
}
.mf-seg input { position: absolute; opacity: 0; pointer-events: none; }
.mf-seg label {
  cursor: pointer;
  padding: 7px 14px;
  border-radius: 7px;
  font-weight: 500;
  font-size: 0.8125rem;
  color: var(--rpd-muted, #5c6b80);
  transition: background 120ms ease, color 120ms ease;
  user-select: none;
  margin: 0;
}
.mf-seg label:hover { color: var(--rpd-ink, #0a1320); }
.mf-seg input:checked + label {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  box-shadow: 0 1px 2px rgba(14, 27, 44, 0.12);
}
.mf-seg input:focus-visible + label {
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
}

/* ---------- Form body ---------- */
form { padding: 22px 26px 8px; }

.mf-group {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--rpd-navy-800, #15273d);
  margin: 22px 0 14px;
}
.mf-group:first-of-type { margin-top: 4px; }
.mf-group__line {
  flex: 1;
  height: 1px;
  background: var(--rpd-line-2, #eff2f6);
}

.mf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  row-gap: 18px;
}
.mf-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.mf-field--span-2 { grid-column: 1 / -1; }
.mf-field label {
  font-weight: 500;
  font-size: 0.8125rem;
  color: var(--rpd-ink, #0a1320);
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.2;
}
.mf-opt {
  font-weight: 400;
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
  letter-spacing: 0;
  text-transform: none;
}

/* ---------- Inputs ---------- */
.mf-input,
.mf-select,
.mf-textarea {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-sm, 6px);
  transition: border-color 120ms ease, box-shadow 120ms ease,
    background 120ms ease;
  appearance: none;
  -webkit-appearance: none;
}
.mf-textarea {
  height: auto;
  min-height: 92px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
}
.mf-input::placeholder,
.mf-textarea::placeholder {
  color: #9aa3b1;
}
.mf-input:hover,
.mf-select:hover,
.mf-textarea:hover {
  border-color: var(--rpd-navy-400, #5a7795);
}
.mf-input:focus,
.mf-select:focus,
.mf-textarea:focus {
  outline: none;
  border-color: var(--rpd-navy-800, #15273d);
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.18);
}
.mf-input[type="date"] { font-variant-numeric: tabular-nums; }

/* ---------- $ affix ---------- */
.mf-affix { position: relative; display: flex; }
.mf-affix__sym {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--rpd-muted, #5c6b80);
  pointer-events: none;
}
.mf-affix .mf-input {
  padding-left: 24px;
  font-variant-numeric: tabular-nums;
}

/* ---------- Select chevron ---------- */
.mf-select {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230E1B2C' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 32px;
}

/* ---------- Dropzone ---------- */
.mf-dropzone {
  position: relative;
  border: 1.5px dashed var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-md, 10px);
  background: var(--rpd-bg, #fafbfc);
  padding: 16px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 0;
  transition: border-color 120ms ease, background 120ms ease;
}
.mf-dropzone:hover {
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-bg-2, #f4f6f9);
}
.mf-dropzone.is-dragover {
  border-color: var(--rpd-coral, #f26a6f);
  background: #fff4f4;
}
.mf-dropzone.has-file {
  border-style: solid;
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-bg-2, #f4f6f9);
}
.mf-dropzone__icon {
  width: 38px;
  height: 38px;
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-bg-2, #f4f6f9);
  color: var(--rpd-navy-800, #15273d);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.mf-dropzone__icon svg { width: 20px; height: 20px; stroke-width: 1.75; }
.mf-dropzone__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
.mf-dropzone__title {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mf-dropzone__link {
  color: var(--rpd-navy-700, #1e3753);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.mf-dropzone__sub {
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
}
.mf-dropzone input[type="file"] {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.mf-dropzone__clear {
  appearance: none;
  background: transparent;
  border: 1px solid var(--rpd-line, #e3e8ef);
  color: var(--rpd-muted, #5c6b80);
  width: 28px;
  height: 28px;
  border-radius: var(--rpd-r-sm, 6px);
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: background 120ms ease, color 120ms ease;
}
.mf-dropzone__clear:hover {
  background: var(--rpd-white, #fff);
  color: var(--rpd-ink, #0a1320);
}
.mf-dropzone__clear svg { width: 14px; height: 14px; stroke-width: 1.75; }

/* ---------- Footer ---------- */
.mf-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 18px 26px;
  margin-top: 8px;
  border-top: 1px solid var(--rpd-line-2, #eff2f6);
  background: var(--rpd-bg, #fafbfc);
  flex-wrap: wrap;
}
.mf-foot__left {
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
  display: flex;
  align-items: center;
  gap: 8px;
}
.mf-foot__left svg { width: 14px; height: 14px; stroke-width: 1.75; }
.mf-foot__actions { display: flex; gap: 10px; }

.mf-btn {
  appearance: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 10px 18px;
  border-radius: var(--rpd-r-sm, 6px);
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 120ms ease, color 120ms ease,
    border-color 120ms ease, box-shadow 120ms ease;
}
.mf-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
}
.mf-btn[disabled] { opacity: 0.65; cursor: not-allowed; }
.mf-btn svg { width: 16px; height: 16px; stroke-width: 1.75; }

.mf-btn--ghost {
  background: transparent;
  color: var(--rpd-ink, #0a1320);
  border-color: var(--rpd-line, #e3e8ef);
}
.mf-btn--ghost:hover {
  background: var(--rpd-white, #fff);
  border-color: var(--rpd-navy-700, #1e3753);
}

.mf-btn--primary {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  border-color: var(--rpd-navy-800, #15273d);
}
.mf-btn--primary:hover {
  background: var(--rpd-navy-700, #1e3753);
  border-color: var(--rpd-navy-700, #1e3753);
}
.mf-btn--primary:active {
  background: var(--rpd-navy-900, #0e1b2c);
}

/* ---------- Success summary ---------- */
.mf-card--success {
  border-color: #cfe7da;
  box-shadow: 0 1px 2px rgba(31, 157, 85, 0.08);
}
.mf-summary {
  padding: 18px 26px 22px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
}
.mf-summary__row {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mf-summary__row dt {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--rpd-muted, #5c6b80);
}
.mf-summary__row dd {
  margin: 0;
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--rpd-ink, #0a1320);
  font-variant-numeric: tabular-nums;
}

/* ---------- Responsive ---------- */
@media (max-width: 720px) {
  .mf-card-head { padding: 18px 18px 14px; }
  form { padding: 18px 18px 6px; }
  .mf-foot { padding: 16px 18px; }
  .mf-grid { grid-template-columns: 1fr; column-gap: 0; }
  .mf-summary { grid-template-columns: 1fr; padding: 16px 18px 18px; }
  .mf-seg { width: 100%; justify-content: stretch; }
  .mf-seg label { flex: 1; text-align: center; }
  .mf-foot__actions { width: 100%; }
  .mf-btn { flex: 1; justify-content: center; }
}

/* ---------- Dark mode ---------- */
html.dark .mls-form-root .mf-card {
  background: var(--rpd-navy-900, #0e1b2c);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
html.dark .mls-form-root .mf-card-head { border-bottom-color: rgba(255, 255, 255, 0.06); }
html.dark .mls-form-root .mf-card-title { color: #fff; }
html.dark .mls-form-root .mf-card-sub { color: #b6c0cf; }
html.dark .mls-form-root .mf-unit-chip {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.10);
  color: #e6ecf3;
}
html.dark .mls-form-root .mf-group { color: #c8d3e0; }
html.dark .mls-form-root .mf-group__line { background: rgba(255, 255, 255, 0.08); }
html.dark .mls-form-root .mf-field label { color: #e6ecf3; }
html.dark .mls-form-root .mf-opt { color: #8a96a8; }
html.dark .mls-form-root .mf-input,
html.dark .mls-form-root .mf-select,
html.dark .mls-form-root .mf-textarea {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
  color: #f0f4f9;
}
html.dark .mls-form-root .mf-input::placeholder,
html.dark .mls-form-root .mf-textarea::placeholder { color: #7a8595; }
html.dark .mls-form-root .mf-input:hover,
html.dark .mls-form-root .mf-select:hover,
html.dark .mls-form-root .mf-textarea:hover { border-color: rgba(255, 255, 255, 0.22); }
html.dark .mls-form-root .mf-input:focus,
html.dark .mls-form-root .mf-select:focus,
html.dark .mls-form-root .mf-textarea:focus {
  border-color: #fff;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.35);
}
html.dark .mls-form-root .mf-select {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23E6ECF3' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>");
}
html.dark .mls-form-root .mf-affix__sym { color: #8a96a8; }
html.dark .mls-form-root .mf-seg {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
}
html.dark .mls-form-root .mf-seg label { color: #b6c0cf; }
html.dark .mls-form-root .mf-seg input:checked + label {
  background: #fff;
  color: var(--rpd-navy-900, #0e1b2c);
}
html.dark .mls-form-root .mf-dropzone {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .mls-form-root .mf-dropzone:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .mls-form-root .mf-dropzone.has-file {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.20);
}
html.dark .mls-form-root .mf-dropzone__icon {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
html.dark .mls-form-root .mf-dropzone__title { color: #f0f4f9; }
html.dark .mls-form-root .mf-dropzone__link { color: #c4d3e6; }
html.dark .mls-form-root .mf-dropzone__sub { color: #8a96a8; }
html.dark .mls-form-root .mf-dropzone__clear {
  border-color: rgba(255, 255, 255, 0.12);
  color: #b6c0cf;
}
html.dark .mls-form-root .mf-foot {
  background: rgba(255, 255, 255, 0.02);
  border-top-color: rgba(255, 255, 255, 0.06);
}
html.dark .mls-form-root .mf-foot__left { color: #8a96a8; }
html.dark .mls-form-root .mf-btn--ghost {
  color: #e6ecf3;
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .mls-form-root .mf-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .mls-form-root .mf-btn--primary {
  background: #fff;
  color: var(--rpd-navy-900, #0e1b2c);
  border-color: #fff;
}
html.dark .mls-form-root .mf-btn--primary:hover {
  background: #e6ecf3;
  border-color: #e6ecf3;
}
html.dark .mls-form-root .mf-summary__row dt { color: #8a96a8; }
html.dark .mls-form-root .mf-summary__row dd { color: #f0f4f9; }
html.dark .mls-form-root.mf-overlay { background: rgba(5, 10, 18, 0.7); }
html.dark .mls-form-root .mf-close {
  border-color: rgba(255, 255, 255, 0.12);
  color: #b6c0cf;
}
html.dark .mls-form-root .mf-close:hover {
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.22);
}
</style>
